import React, { useState, useCallback } from 'react';
import { PrefectureEditorProps, FormErrors } from './types';
import BasicInfoSection from './sections/BasicInfoSection';
import BulkAssignSection from './sections/BulkAssignSection';

type TabType = 'basic' | 'bulk_assign';

const PrefectureEditor: React.FC<PrefectureEditorProps> = ({
  prefecture: initialPrefecture,
  updateUrl,
  bulkAssignUrl,
  searchJobSourcesUrl,
  csrfToken,
  backUrl,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState(initialPrefecture);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    setSuccessMessage(null);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        const value = formData[key as keyof typeof formData];
        if (value === null || value === undefined) return;

        formDataToSend.append(`prefecture[${key}]`, String(value));
      });

      const response = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: formDataToSend,
      });

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();

        if (response.ok && result.success) {
          setSuccessMessage(result.message || '都道府県を更新しました');
          // フォームデータを更新（サーバーから返されたデータがあれば使用）
          if (result.prefecture) {
            setFormData(result.prefecture);
          }
        } else {
          setErrors(result.errors || {});
          setSuccessMessage(null);
        }
      } else {
        throw new Error('予期しないレスポンス形式です');
      }
    } catch (error) {
      setErrors({ base: [error instanceof Error ? error.message : '更新に失敗しました'] });
      setSuccessMessage(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, updateUrl, csrfToken]);

  const tabs = [
    { id: 'basic' as TabType, label: '基本情報' },
    { id: 'bulk_assign' as TabType, label: 'エージェント一括付与' },
  ];

  return (
    <div className="container-fluid">
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage(null)}
            aria-label="Close"
          />
        </div>
      )}

      {Object.keys(errors).length > 0 && errors.base && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {errors.base.join(', ')}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrors({})}
            aria-label="Close"
          />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm">
          <div className="card-header bg-white">
            <ul className="nav nav-tabs card-header-tabs" role="tablist">
              {tabs.map((tab) => (
                <li key={tab.id} className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-body">
            <div className="tab-content">
              {activeTab === 'basic' && (
                <BasicInfoSection
                  formData={formData}
                  setFormData={setFormData}
                  errors={errors}
                />
              )}
              {activeTab === 'bulk_assign' && (
                <BulkAssignSection
                  bulkAssignUrl={bulkAssignUrl}
                  searchJobSourcesUrl={searchJobSourcesUrl}
                  prefectureId={formData.id}
                  prefectureName={formData.name}
                  csrfToken={csrfToken}
                />
              )}
            </div>
          </div>
          {activeTab === 'basic' && (
            <div className="card-footer bg-white">
              <div className="d-flex justify-content-end gap-2">
                <a href={backUrl} className="btn btn-secondary">
                  キャンセル
                </a>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                  style={{ backgroundColor: '#00bb94', borderColor: '#00bb94' }}
                >
                  {isSubmitting ? '保存中...' : '更新'}
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default PrefectureEditor;

